import { Close } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import IconPickerDialog from "../../../components/IconPickerDialog";
import LucideIcon from "../../../components/LucideIcon";
import { hostname } from "../../../utils";

export interface WhitelabelApp {
  id: string;
  name: string;
  icon: string;
  url?: string;
  appId?: string;
  enabled: boolean;
}

interface PublicApp {
  id: string;
  name: string;
  icon?: string | null;
  description?: string | null;
}

interface AppFormDialogProps {
  open: boolean;
  app: WhitelabelApp | null;
  onClose: () => void;
  onSubmit: (app: WhitelabelApp) => void | Promise<void>;
  getToken: () => Promise<string>;
}

const DEFAULT_ICON = "AppWindowMac";

type SourceMode = "url" | "platform";

const AppFormDialog: React.FC<AppFormDialogProps> = ({
  open,
  app,
  onClose,
  onSubmit,
  getToken,
}) => {
  const [mode, setMode] = useState<SourceMode>("url");
  const [name, setName] = useState("");
  const [icon, setIcon] = useState(DEFAULT_ICON);
  const [url, setUrl] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [publicApps, setPublicApps] = useState<PublicApp[]>([]);
  const [publicAppsLoading, setPublicAppsLoading] = useState(false);
  const [publicAppsError, setPublicAppsError] = useState<string | null>(null);
  const [selectedPublicApp, setSelectedPublicApp] = useState<PublicApp | null>(
    null
  );

  const isEditing = !!app;

  useEffect(() => {
    if (open) {
      const initialMode: SourceMode = app?.appId ? "platform" : "url";
      setMode(initialMode);
      setName(app?.name || "");
      setIcon(app?.icon || DEFAULT_ICON);
      setUrl(app?.url || "");
      setEnabled(app?.enabled !== undefined ? app.enabled : true);
      setSelectedPublicApp(
        app?.appId
          ? {
              id: app.appId,
              name: app.name,
              icon: app.icon,
            }
          : null
      );
      setError(null);
      setSubmitting(false);
    }
  }, [open, app]);

  // Lazy-load the public-app picker only when the user switches to that mode.
  useEffect(() => {
    if (!open || mode !== "platform" || publicApps.length > 0) return;
    let cancelled = false;
    (async () => {
      setPublicAppsLoading(true);
      setPublicAppsError(null);
      try {
        const token = await getToken();
        const resp = await axios.get(`${hostname}/apps/public-shareable`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setPublicApps(Array.isArray(resp.data?.apps) ? resp.data.apps : []);
        }
      } catch (err: any) {
        if (!cancelled) {
          setPublicAppsError(
            err.response?.data?.error || "Failed to load public apps"
          );
        }
      } finally {
        if (!cancelled) setPublicAppsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, mode, getToken, publicApps.length]);

  const handlePickPublicApp = (next: PublicApp | null) => {
    setSelectedPublicApp(next);
    if (next) {
      // Seed name/icon from the source app so the admin doesn't have to type
      // it again. They can still edit before saving.
      if (!name.trim()) setName(next.name);
      if (next.icon) setIcon(next.icon);
    }
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Name is required");
      return;
    }

    if (mode === "url") {
      const trimmedUrl = url.trim();
      if (!trimmedUrl) {
        setError("URL is required");
        return;
      }
      if (
        !trimmedUrl.startsWith("http://") &&
        !trimmedUrl.startsWith("https://")
      ) {
        setError("URL must start with http:// or https://");
        return;
      }
    } else {
      if (!selectedPublicApp) {
        setError("Pick a public app to add");
        return;
      }
    }

    setError(null);
    setSubmitting(true);
    try {
      const id =
        app?.id ||
        (typeof crypto !== "undefined" && "randomUUID" in crypto
          ? (crypto as any).randomUUID()
          : `app_${Date.now()}_${Math.random().toString(36).slice(2)}`);
      const next: WhitelabelApp = {
        id,
        name: trimmedName,
        icon: icon || DEFAULT_ICON,
        enabled,
      };
      if (mode === "url") {
        next.url = url.trim();
      } else if (selectedPublicApp) {
        next.appId = selectedPublicApp.id;
      }
      await onSubmit(next);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {isEditing ? "Edit App" : "Add App"}
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Tabs
            value={mode}
            onChange={(_, v) => setMode(v as SourceMode)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ mb: 2 }}
          >
            <Tab value="url" label="External URL" />
            <Tab value="platform" label="Public Platform App" />
          </Tabs>

          <Stack spacing={2}>
            {mode === "platform" && (
              <>
                {publicAppsError && (
                  <Alert severity="error">{publicAppsError}</Alert>
                )}
                <Autocomplete
                  options={publicApps}
                  loading={publicAppsLoading}
                  value={selectedPublicApp}
                  onChange={(_, v) => handlePickPublicApp(v)}
                  getOptionLabel={(o) => o.name || ""}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <Box sx={{ mr: 1, display: "flex" }}>
                        <LucideIcon
                          name={option.icon || DEFAULT_ICON}
                          size={18}
                        />
                      </Box>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        {option.description && (
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Public app"
                      helperText="Only Public apps from your companies show up here. Set an app's sharing to Public in the App Builder to publish it."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {publicAppsLoading ? (
                              <CircularProgress size={16} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </>
            )}

            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 48,
                  height: 48,
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  backgroundColor: "action.hover",
                }}
              >
                <LucideIcon name={icon} size={24} />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Icon: <strong>{icon}</strong>
                </Typography>
                <Button
                  size="small"
                  onClick={() => setIconPickerOpen(true)}
                  sx={{ mt: 0.5 }}
                >
                  Choose Icon
                </Button>
              </Box>
            </Stack>

            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              autoFocus
              required
              inputProps={{ maxLength: 100 }}
              helperText="Shown in the user's side menu"
            />

            {mode === "url" && (
              <TextField
                label="URL"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                fullWidth
                required
                placeholder="https://example.com/embed"
                helperText="The page must allow being embedded in an iframe (X-Frame-Options / CSP frame-ancestors)"
              />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
              }
              label="Enabled"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? "Saving..." : isEditing ? "Save" : "Add App"}
          </Button>
        </DialogActions>
      </Dialog>

      <IconPickerDialog
        open={iconPickerOpen}
        value={icon}
        onClose={() => setIconPickerOpen(false)}
        onSelect={(name) => setIcon(name)}
      />
    </>
  );
};

export default AppFormDialog;
